import { Box, Button, Chip, Divider, GlobalStyles, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import PrintIcon from '@mui/icons-material/Print'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useCompanies, useEnquiry, usePackageSlip, useSessionTypes } from '@/api/hooks'
import { useAuthStore } from '@/auth/authStore'
import type { SlipLine, SlipPackage } from '@/api/types'
import { EmptyState, ErrorBanner, InlineSpinner, formatDate, humanise } from '@/components/common'
import { SendDocumentButton } from '@/components/SendDocument'

type Language = 'en' | 'ta'

/**
 * The sheet handed to somebody who has asked what we do.
 *
 * <p>Printed from the browser rather than drawn as a PDF on the server: the
 * dishes are named in Tamil, and Tamil needs its vowel signs reordered before
 * they are drawn. A browser does that correctly and the PDF library we print
 * every other document with does not.</p>
 */

/** Everything on the sheet that is not a dish name, in both languages. */
const TEXT: Record<Language, Record<string, string>> = {
  en: {
    title: 'Menu sheet',
    enquiry: 'Enquiry',
    customer: 'Customer',
    occasion: 'Occasion',
    date: 'Date',
    guests: 'Guests',
    service: 'Service',
    perPerson: 'per person',
    minimum: 'minimum',
    notes: 'Important notes',
    terms: 'Terms and conditions',
    scanTerms: 'Scan for Terms and Conditions',
    draft: 'Draft menu',
    footer: 'NB : GST, Transport Charges, Service Charges are additionally applicable',
    print: 'Print',
    nothing: 'No published package for this service type yet.',
    language: 'English',
  },
  ta: {
    title: 'மெனு விவரம்',
    enquiry: 'விசாரணை',
    customer: 'வாடிக்கையாளர்',
    occasion: 'நிகழ்ச்சி',
    date: 'தேதி',
    guests: 'நபர்கள்',
    service: 'சேவை',
    perPerson: 'ஒருவருக்கு',
    minimum: 'குறைந்தபட்சம்',
    notes: 'முக்கிய குறிப்புகள்',
    terms: 'விதிமுறைகள்',
    scanTerms: 'விதிமுறைகளை ஸ்கேன் செய்யவும்',
    draft: 'வரைவு மெனு',
    footer: 'குறிப்பு : GST, போக்குவரத்து கட்டணம், சேவைக் கட்டணம் தனியாக வசூலிக்கப்படும்',
    print: 'அச்சிடு',
    nothing: 'இந்த சேவைக்கு வெளியிடப்பட்ட மெனு எதுவும் இல்லை.',
    language: 'தமிழ்',
  },
}

/**
 * A last-resort order for sittings, in the order a day is eaten.
 *
 * <p>Which sittings exist is the company's own master, and that is what orders
 * the sheet. This is only for a sitting a package names that the master has
 * since stopped keeping, so it still prints somewhere sensible rather than
 * first.</p>
 */
const SESSION_ORDER = ['BREAKFAST', 'LUNCH', 'SNACKS', 'HI_TEA', 'DINNER', 'MIDNIGHT', 'ANY']

/**
 * Tamil for the sittings this business keeps.
 *
 * <p>A sitting the office adds for itself carries only the name it was given,
 * which is what the sheet then shows in either language.</p>
 */
const SESSION_NAMES: Record<Language, Record<string, string>> = {
  en: {
    BREAKFAST: 'Breakfast',
    LUNCH: 'Lunch',
    SNACKS: 'Snacks',
    HI_TEA: 'Evening tiffin',
    DINNER: 'Dinner',
    MIDNIGHT: 'Midnight',
    ANY: 'Any session',
  },
  ta: {
    BREAKFAST: 'காலை டிபன்',
    LUNCH: 'மதியம் சாப்பாடு',
    SNACKS: 'சிற்றுண்டி',
    HI_TEA: 'மாலை டிபன்',
    DINNER: 'இரவு உணவு',
    MIDNIGHT: 'நள்ளிரவு உணவு',
    ANY: 'எந்த வேளையும்',
  },
}

const SERVICE_NAMES: Record<string, string> = {
  BANQUET: 'மண்டப நிகழ்ச்சி',
  OUTDOOR_CATERING: 'வெளியிட கேட்டரிங்',
  BULK_TAKEAWAY: 'மொத்த பார்சல்',
}

/** ₹180, not ₹180.00: a rate card is read at a glance. */
const rupees = (value: number) => `₹${Math.round(value).toLocaleString('en-IN')}`

/**
 * One line of a package as it is read aloud.
 *
 * <p>"Idli – 2 / Idiyappam – 2" where each dish carries its own servings, and
 * "Kootu – 2" where the line is a choice of two out of a category. A line that
 * names no dish at all is left as its label, which is what a package still
 * being written looks like.</p>
 */
function lineText(line: SlipLine, language: Language) {
  const dishes = line.choices.map((choice) => {
    const name = (language === 'ta' ? choice.nameLocal : null) ?? choice.name
    return choice.portion && choice.portion > 1 ? `${name} – ${choice.portion}` : name
  })
  if (dishes.length === 0) {
    return line.count > 1 ? `${line.label} – ${line.count}` : line.label
  }
  // The count belongs to the line, not to any one dish on it, so it goes after
  // the whole list: two of these five, not two of the last one.
  return line.count > 1 && dishes.length > 1
    ? `${dishes.join(' / ')} – ${line.count}`
    : dishes.join(' / ')
}

function PackageCard({ pack, language }: { pack: SlipPackage; language: Language }) {
  const text = TEXT[language]
  const title = (language === 'ta' ? pack.nameLocal : null) ?? pack.name
  return (
    <Box
      className="slip-card"
      sx={{
        border: '1px solid',
        borderColor: 'warning.main',
        borderRadius: 2,
        overflow: 'hidden',
        breakInside: 'avoid',
        bgcolor: 'background.paper',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        sx={{ px: 1.5, py: 0.75, bgcolor: 'warning.main', color: 'common.white' }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {/* Marked for whoever is printing, not for the customer: a menu still
              in draft is worth knowing about before it is handed over, and
              worth nothing on the paper itself. */}
          {pack.draft ? (
            <Chip
              className="no-print"
              size="small"
              variant="outlined"
              label={text.draft}
              sx={{ borderColor: 'common.white', color: 'common.white' }}
            />
          ) : null}
          <Chip
            size="small"
            label={rupees(pack.pricePerPerson)}
            sx={{ bgcolor: 'common.white', color: 'text.primary', fontWeight: 700 }}
          />
        </Stack>
      </Stack>
      <Box sx={{ px: 1.5, py: 1 }}>
        {pack.lines.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        ) : (
          pack.lines.map((line) => (
            <Typography key={line.number} variant="body2" sx={{ lineHeight: 1.7 }}>
              {line.number}. {lineText(line, language)}
            </Typography>
          ))
        )}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {rupees(pack.pricePerPerson)} {text.perPerson}
          {pack.minimumGuaranteeGuests > 0
            ? ` · ${text.minimum} ${pack.minimumGuaranteeGuests}`
            : ''}
        </Typography>
      </Box>
    </Box>
  )
}

/** What the customer asked for, at the head of the sheet they are handed. */
function Fact({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </Box>
  )
}

export function EnquirySlipPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  // The language is in the address, so a sheet somebody printed in Tamil is
  // reopened in Tamil rather than in whatever the last person chose.
  const [searchParams, setSearchParams] = useSearchParams()
  const language: Language = searchParams.get('lang') === 'ta' ? 'ta' : 'en'
  const setLanguage = (next: Language) => setSearchParams(next === 'ta' ? { lang: 'ta' } : {})
  const { data: enquiry, isLoading, error } = useEnquiry(id)
  const { data: sheet, error: slipError } = usePackageSlip(enquiry?.serviceType)
  // The sittings this business sells for this line of business, in the order it
  // keeps them - so a company that adds a snacks round gets a snacks section
  // without anybody editing this page.
  const { data: sittings } = useSessionTypes({ serviceType: enquiry?.serviceType })
  const { data: companies } = useCompanies()
  // The business being worked in, which is the business whose enquiry this is:
  // everything on screen belongs to the company in force.
  const activeCompanyId = useAuthStore((state) => state.activeCompanyId ?? state.user?.companyId)

  if (isLoading) return <InlineSpinner />
  if (error) return <ErrorBanner error={error} />
  if (!enquiry) return <EmptyState message="Enquiry not found." />

  const text = TEXT[language]
  const company = companies?.find((candidate) => candidate.id === activeCompanyId)
  const serviceName =
    language === 'ta'
      ? (SERVICE_NAMES[enquiry.serviceType] ?? humanise(enquiry.serviceType))
      : humanise(enquiry.serviceType)

  const packages = sheet?.packages ?? []
  const terms = sheet?.terms ?? []
  // The words, a code, or both - answered per line of business in Settings and
  // decided by the server, so the sheet and the quotation cannot disagree.
  const termsQr = sheet?.termsQr ?? null

  // Grouped the way the sheet is read: the sittings the office keeps, in its
  // own order, then anything a package names that the master no longer does -
  // so no package is left off the sheet because a sitting was renamed.
  const known = (sittings ?? []).map((sitting) => sitting.code)

  /**
   * Where a sitting belongs on the sheet.
   *
   * <p>A day is read breakfast, lunch, hi-tea, dinner, whatever order the
   * sittings master keeps them in. Sittings outside that run follow it, in the
   * order the master keeps them.</p>
   */
  const rankOf = (session: string) => {
    const hour = SESSION_ORDER.indexOf(session)
    if (hour >= 0) return hour
    const kept = known.indexOf(session)
    return SESSION_ORDER.length + (kept >= 0 ? kept : known.length)
  }

  const sessions = [...known, ...packages.map((pack) => pack.sessionType)]
    .filter((session, index, all) => all.indexOf(session) === index)
    .sort((left, right) => rankOf(left) - rankOf(right))

  const sittingName = (code: string) =>
    SESSION_NAMES[language][code] ??
    (sittings ?? []).find((sitting) => sitting.code === code)?.name ??
    humanise(code)

  const groups = sessions
    .map((session) => ({
      session,
      list: packages
        .filter((pack) => pack.sessionType === session)
        .sort((a, b) => a.pricePerPerson - b.pricePerPerson),
    }))
    .filter((group) => group.list.length > 0)

  return (
    <>
      <GlobalStyles
        styles={{
          '@media print': {
            '@page': { size: 'A4 portrait', margin: '10mm' },
            '.no-print': { display: 'none !important' },
            body: { background: '#ffffff' },
            '.slip-card': { breakInside: 'avoid' },
            '.slip-group': { breakInside: 'avoid-page' },
          },
        }}
      />

      <Stack
        className="no-print"
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ p: 2 }}
      >
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/enquiries/${enquiry.id}`)}>
          Back to enquiry
        </Button>
        <Stack direction="row" spacing={1} alignItems="center">
          {/* The sheet is handed to the customer, so it is printed in the
              language they read, not the one the office works in. */}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={language}
            onChange={(_event, value) => value && setLanguage(value as Language)}
          >
            <ToggleButton value="en">English</ToggleButton>
            <ToggleButton value="ta">தமிழ்</ToggleButton>
          </ToggleButtonGroup>
          <SendDocumentButton kind="menu-sheet" id={enquiry.id} size="medium" />
          <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
            {text.print}
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ maxWidth: 1000, mx: 'auto', px: { xs: 2, print: 0 }, pb: 4 }}>
        <ErrorBanner error={slipError} />

        <Stack spacing={0.5} sx={{ mb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {company?.name ?? ''}
          </Typography>
          <Typography variant="subtitle1" color="warning.main" sx={{ fontWeight: 700 }}>
            {text.title} · {serviceName}
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
            gap: 1,
            p: 1.5,
            mb: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Fact label={text.enquiry} value={enquiry.enquiryNumber} />
          <Fact label={text.customer} value={enquiry.customerName} />
          <Fact label={text.occasion} value={humanise(enquiry.eventType)} />
          <Fact label={text.date} value={formatDate(enquiry.eventDate)} />
          <Fact
            label={text.guests}
            value={enquiry.expectedGuestCount > 0 ? String(enquiry.expectedGuestCount) : '—'}
          />
        </Box>

        {groups.length === 0 ? (
          <EmptyState message={text.nothing} />
        ) : (
          groups.map((group) => (
            <Box key={group.session} className="slip-group" sx={{ mb: 2 }}>
              <Divider textAlign="left" sx={{ mb: 1 }}>
                <Typography variant="overline" sx={{ fontWeight: 700 }}>
                  {sittingName(group.session)}
                </Typography>
              </Divider>
              <Box
                sx={{
                  display: 'grid',
                  // auto-fit, not a fixed three: a sitting that sells one menu
                  // gets a card the width of the sheet rather than a narrow
                  // column with every line wrapped twice.
                  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                  gap: 1.5,
                  alignItems: 'start',
                }}
              >
                {group.list.map((pack) => (
                  <PackageCard key={pack.id} pack={pack} language={language} />
                ))}
              </Box>
            </Box>
          ))
        )}

        {/* Empty on purpose: what was agreed at the counter is written here by
            hand, on the sheet the customer takes away. */}
        <Box sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{ px: 1.5, py: 0.75, bgcolor: 'warning.main', color: 'common.white', fontWeight: 700 }}
          >
            {text.notes}
          </Typography>
          <Box sx={{ height: 96 }} />
        </Box>

        {/* What the prices above assume. Written per line of business in
            Settings; a sheet with none configured simply has none. */}
        {terms.length > 0 || termsQr ? (
          <Box className="slip-group" sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{ px: 1.5, py: 0.75, bgcolor: 'warning.main', color: 'common.white', fontWeight: 700 }}
            >
              {text.terms}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: terms.length > 0 ? 0 : 1.5 }}>
              {terms.length > 0 ? (
                <Box component="ol" sx={{ m: 0, px: 3, py: 1.5, flex: 1 }}>
                  {terms.map((clause, index) => (
                    <Typography
                      component="li"
                      variant="body2"
                      key={`${clause.termType}-${index}`}
                      sx={{ lineHeight: 1.6, mb: 0.5 }}
                    >
                      {clause.content}
                    </Typography>
                  ))}
                </Box>
              ) : null}
              {termsQr ? (
                <Box sx={{ py: 1.5, textAlign: 'center', mx: terms.length > 0 ? 0 : 'auto' }}>
                  <Box
                    component="img"
                    src={termsQr}
                    alt="Scan for the terms and conditions"
                    sx={{ width: 96, height: 96 }}
                  />
                  <Typography variant="caption" component="div" color="text.secondary">
                    {text.scanTerms}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </Box>
        ) : null}

        <Typography
          variant="body2"
          align="center"
          sx={{ mt: 2, py: 1, fontWeight: 700, bgcolor: 'warning.main', color: 'common.white', borderRadius: 1 }}
        >
          {text.footer}
        </Typography>
      </Box>
    </>
  )
}
